import { getCurrentSession } from './auth';

export type FeedbackCategory =
  | 'BUG_REPORT'
  | 'INCORRECT_DATA'
  | 'USABILITY_PROBLEM'
  | 'FEATURE_SUGGESTION'
  | 'PERFORMANCE_ISSUE'
  | 'GENERAL_FEEDBACK';

export type FeedbackSource = 'BETA_TESTER' | 'REGULAR_CUSTOMER';

export type FeedbackStatus = 'NEW' | 'REVIEWING' | 'FIXED' | 'CLOSED';
export type FeedbackPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export interface FeedbackAnnotation {
  id: string;
  index: number;
  elementSelector: string;
  comment: string;
  screenshotId: string | null;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface FeedbackScreenshot {
  id: string;
  dataUrl: string;
  mimeType: 'image/jpeg' | 'image/png';
  width: number;
  height: number;
}

export interface FeedbackTechnicalMetadata {
  userAgent: string;
  viewportWidth: number;
  viewportHeight: number;
  devicePixelRatio: number;
  timestamp: string;
}

export interface SubmitFeedbackPayload {
  tenantId?: string | null;
  source: FeedbackSource;
  category: FeedbackCategory;
  description: string;
  pageRoute: string;
  annotations: FeedbackAnnotation[];
  screenshots: FeedbackScreenshot[];
  idempotencyKey: string;
  metadata: FeedbackTechnicalMetadata;
  screenshotBase64?: string | null;
  screenshotMimeType?: string;
}

export interface FeedbackItem {
  id: string;
  tenantId: string;
  userId: string;
  testerName: string | null;
  testerEmail: string | null;
  category: FeedbackCategory;
  source: FeedbackSource;
  description: string;
  status: FeedbackStatus;
  priority: FeedbackPriority;
  pageRoute: string | null;
  annotations: FeedbackAnnotation[];
  screenshotPath: string | null;
  screenshotUrl: string | null;
  screenshots?: Array<{ id: string; screenshotPath: string; screenshotUrl: string | null; mimeType: string; byteLength: number }>;
  metadata: FeedbackTechnicalMetadata;
  adminNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FeedbackListResponse {
  ok: boolean;
  items: FeedbackItem[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  metrics: {
    total: number;
    new: number;
    reviewing: number;
    fixed: number;
    closed: number;
    critical: number;
  };
}

async function getAuthHeader(): Promise<Record<string, string>> {
  const session = await getCurrentSession();
  const token = session?.access_token;
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

/**
 * Checks if the current authenticated user has active beta tester entitlement or admin override.
 */
export async function checkBetaFeedbackEligibility(tenantId?: string | null): Promise<boolean> {
  return checkFeedbackEligibility(tenantId, 'BETA_TESTER');
}

export async function checkFeedbackEligibility(tenantId: string | null | undefined, source: FeedbackSource): Promise<boolean> {
  try {
    const authHeaders = await getAuthHeader();
    if (!authHeaders.Authorization) return false;

    const searchParams = new URLSearchParams({ source });
    if (tenantId) searchParams.set('tenantId', tenantId);
    const res = await fetch(`/api/feedback/eligibility?${searchParams.toString()}`, {
      headers: {
        ...authHeaders,
        ...(tenantId ? { 'x-tenant-id': tenantId } : {}),
      },
    });

    if (!res.ok) return false;
    const data = await res.json();
    return Boolean(data.eligible && data.source === source);
  } catch {
    return false;
  }
}

/**
 * Submits feedback and any user-approved, sanitized screenshot attachments.
 */
export async function submitBetaFeedback(payload: SubmitFeedbackPayload): Promise<FeedbackItem> {
  if (payload.screenshots.length > 5) {
    throw new Error('A feedback submission can include no more than 5 screenshots.');
  }
  const authHeaders = await getAuthHeader();
  if (!authHeaders.Authorization) {
    throw new Error('Authentication required to submit beta feedback.');
  }

  const res = await fetch('/api/feedback/submit', {
    method: 'POST',
    headers: {
      ...authHeaders,
      'Content-Type': 'application/json',
      ...(payload.tenantId ? { 'x-tenant-id': payload.tenantId } : {}),
    },
    body: JSON.stringify(payload),
  });

  const data = await res.json();
  if (!res.ok || !data.ok) {
    throw new Error(data.detail || data.error || 'Failed to submit beta feedback.');
  }

  return data.feedback;
}

/**
 * Admin: Fetches the count of NEW submissions for the badge.
 */
export async function fetchNewFeedbackBadgeCount(tenantId?: string | null): Promise<number> {
  try {
    const authHeaders = await getAuthHeader();
    if (!authHeaders.Authorization) return 0;

    const query = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
    const res = await fetch(`/api/admin/feedback/badge${query}`, {
      headers: authHeaders,
    });

    if (!res.ok) return 0;
    const data = await res.json();
    return Number(data.newCount || 0);
  } catch {
    return 0;
  }
}

/**
 * Admin: Lists feedback with filters and metrics.
 */
export async function fetchAdminFeedbackList(params: {
  tenantId?: string | null;
  source?: FeedbackSource;
  status?: string;
  priority?: string;
  category?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<FeedbackListResponse> {
  const authHeaders = await getAuthHeader();
  const searchParams = new URLSearchParams();

  if (params.tenantId) searchParams.set('tenantId', params.tenantId);
  if (params.source) searchParams.set('source', params.source);
  if (params.status) searchParams.set('status', params.status);
  if (params.priority) searchParams.set('priority', params.priority);
  if (params.category) searchParams.set('category', params.category);
  if (params.search) searchParams.set('search', params.search);
  if (params.page) searchParams.set('page', String(params.page));
  if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));

  const res = await fetch(`/api/admin/feedback/items?${searchParams.toString()}`, {
    headers: authHeaders,
  });

  const data = await res.json();
  if (!res.ok || !data.ok) {
    throw new Error(data.detail || data.error || 'Failed to fetch feedback items.');
  }

  return data;
}

/**
 * Admin: Fetches feedback details with private signed URL.
 */
export async function fetchAdminFeedbackDetail(feedbackId: string): Promise<FeedbackItem> {
  const authHeaders = await getAuthHeader();
  const res = await fetch(`/api/admin/feedback/items/${encodeURIComponent(feedbackId)}`, {
    headers: authHeaders,
  });

  const data = await res.json();
  if (!res.ok || !data.ok) {
    throw new Error(data.detail || data.error || 'Failed to fetch feedback details.');
  }

  return data.feedback;
}

/**
 * Admin: Updates feedback status, priority, or notes.
 */
export async function updateAdminFeedback(
  feedbackId: string,
  updates: { status?: FeedbackStatus; priority?: FeedbackPriority; adminNotes?: string }
): Promise<FeedbackItem> {
  const authHeaders = await getAuthHeader();
  const res = await fetch(`/api/admin/feedback/items/${encodeURIComponent(feedbackId)}`, {
    method: 'PATCH',
    headers: {
      ...authHeaders,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });

  const data = await res.json();
  if (!res.ok || !data.ok) {
    throw new Error(data.detail || data.error || 'Failed to update feedback item.');
  }

  return data.feedback;
}

/**
 * Admin: Deletes selected feedback items and their associated screenshot attachments.
 */
export async function deleteAdminFeedback(feedbackIds: string[]): Promise<number> {
  const authHeaders = await getAuthHeader();
  const res = await fetch('/api/admin/feedback/items', {
    method: 'DELETE',
    headers: {
      ...authHeaders,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ids: feedbackIds }),
  });

  const data = await res.json();
  if (!res.ok || !data.ok) {
    throw new Error(data.detail || data.error || 'Failed to delete selected feedback items.');
  }

  return Number(data.deletedCount ?? feedbackIds.length);
}
